import { useRef, useState, type FormEvent, type InputHTMLAttributes } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { motion, type Variants } from "motion/react";
import { useAuth } from "../auth.tsx";
import { useAction } from "../api.ts";
import { Button, cn, ErrorText, Field, Icon, Input } from "../ui/index.tsx";
import { AuthLayout } from "../layouts.tsx";
import { EASE, useCalm } from "./member/kit.tsx";

const safeNext = (v: string | null) => (v && v.startsWith("/") && !v.startsWith("//") ? v : "/home");

/** Row variants for the stagger container in AuthLayout; shared with Register. */
export const authItem: Variants = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: EASE } } };

/**
 * Password input with a show/hide toggle. `label` is the field's accessible name: Field is a <label>, so
 * without it the toggle's own aria-label would leak into the input's name.
 */
export function PasswordInput({ label, className, ...rest }: { label: string } & Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Input {...rest} type={shown ? "text" : "password"} aria-label={label} className={cn("pr-10", className)} />
      <button type="button" aria-label={shown ? "Hide password" : "Show password"} aria-pressed={shown} onClick={() => setShown((s) => !s)}
        className="absolute right-1 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-md text-faint transition-colors hover:text-fg focus-visible:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60">
        <span className="relative grid place-items-center">
          <Icon name="eye" className="size-4" />
          <span className={cn("absolute h-px w-5 -rotate-45 bg-current transition-opacity", shown ? "opacity-100" : "opacity-0")} />
        </span>
      </button>
    </div>
  );
}

export default function Login() {
  const { me, login, setupPassword } = useAuth();
  const calm = useCalm();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const [shake, setShake] = useState(0);
  const [granted, setGranted] = useState(false);
  // login() sets `me` before it resolves, so hold the redirect while the "access granted" moment plays.
  const holding = useRef(false);

  const signIn = useAction(async () => {
    holding.current = !calm;
    try {
      const r = await login(username.trim(), password);
      if (r.kind === "setup") { setSetupToken(r.setupToken); return; }
      if (holding.current) {
        setGranted(true);
        await new Promise((done) => setTimeout(done, 650));
      }
    } catch (e) {
      setShake((n) => n + 1);
      throw e;
    } finally {
      holding.current = false;
      setGranted(false);
    }
  });
  const setup = useAction(async () => { await setupPassword(setupToken!, newPassword); });

  if (me && !holding.current) return <Navigate to={next} replace />;

  const onSignIn = (e: FormEvent) => { e.preventDefault(); void signIn.run(); };
  const onSetup = (e: FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirm) { setMismatch(true); return; }
    setMismatch(false);
    void setup.run();
  };

  if (setupToken) {
    return (
      <AuthLayout title="Choose a password"
        subtitle="Your account moved to passwords. Your old license key worked one last time — choose a password."
        footer={<button type="button" className="hover:text-fg" onClick={() => { setSetupToken(null); setPassword(""); }}>Back to sign in</button>}>
        <form onSubmit={onSetup} className="space-y-4">
          <motion.div variants={authItem}>
            <Field label="New password" hint="8 to 128 characters">
              <PasswordInput label="New password" autoComplete="new-password" autoFocus required minLength={8} maxLength={128} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </Field>
          </motion.div>
          <motion.div variants={authItem}>
            <Field label="Confirm password" error={mismatch ? "Passwords don't match" : null}>
              <PasswordInput label="Confirm password" autoComplete="new-password" required minLength={8} maxLength={128} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </Field>
          </motion.div>
          <div role="alert" className="empty:hidden"><ErrorText>{setup.error}</ErrorText></div>
          <motion.div variants={authItem}>
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={setup.pending}>Set password and sign in</Button>
          </motion.div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your ExoWare account." shake={shake} granted={granted}
      footer={<>New here? <Link to="/register" className="text-accent hover:underline">Create an account</Link></>}>
      <form onSubmit={onSignIn} className="space-y-4">
        <motion.div variants={authItem}>
          <Field label="Username">
            <Input autoComplete="username" autoFocus required value={username} onChange={(e) => setUsername(e.target.value)} />
          </Field>
        </motion.div>
        <motion.div variants={authItem}>
          <Field label="Password">
            <PasswordInput label="Password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <div className="mt-1.5 text-right text-xs"><Link to="/forgot" className="text-muted hover:text-accent">Forgot password?</Link></div>
        </motion.div>
        <div role="alert" className="empty:hidden"><ErrorText>{signIn.error}</ErrorText></div>
        <motion.div variants={authItem}>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={signIn.pending}>Sign in</Button>
        </motion.div>
      </form>
    </AuthLayout>
  );
}
